import * as THREE from 'three';
import async from 'async-es';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { WebglScene } from "../webgl/scene";
import { ReplaceCoatingCommand } from './commands/ReplaceCoatingCommand';
import { ReplaceKitchenCoatingCommand } from './commands/ReplaceKitchenCoatingCommand';
import { ChangeKitchenStyleCommand } from './commands/ChangeKitchenStyleCommand';
import { ReplaceArrangementCommand } from './commands/ReplaceArrangementCommand';
import { WebglFurniture } from '../webgl/furniture';
import { ArrangementGroup, ArrangementObject, Coating, Commands, ComponentConstants, Entity, Events, eventsManager, SceneConstants, TechnicalElement } from '@rhinov/savane-js';
import { _ASSET_TYPE, createAssetEntity } from '@rhinov/asset-manager-service';

export class InteractiveProjectHandler {

    private divWebgl: HTMLElement;
    private scene: WebglScene;
    private hoverOutlinePass: OutlinePass;
    selectedNode: any[];

    constructor(divWebgl: HTMLElement, scene: WebglScene) {
        this.divWebgl = divWebgl;
        this.scene = scene;
        this.hoverOutlinePass = new OutlinePass(new THREE.Vector2(this.divWebgl.clientWidth, this.divWebgl.clientHeight), this.scene.threeScene, this.scene.defaultCamera);
        this.hoverOutlinePass.visibleEdgeColor = new THREE.Color("#8EC5B7").convertSRGBToLinear();
        this.scene.composer.addPass(this.hoverOutlinePass);
        this.scene.gizmo["enabled"] = true;
        this.scene.gizmo.space = 'local';
        this.scene.gizmo.size = 1.3;

        let gizmoItemsToRemove: any[] = [];
        this.scene.gizmo.traverse(function(item: any) {
            if (item.isMesh && item.name !== 'X' && item.name !== 'Y' && item.name !== 'Z' && item.type !== 'TransformControlsPlane') {
                gizmoItemsToRemove.push(item);
            }
        });
        gizmoItemsToRemove.forEach(element => {
            element.removeFromParent();
        });

        this.selectedNode = [];

        eventsManager.instance.addListener(Events.SELECTED_ARRANGEMENT_REPLACED, (event: any) => {
            this.ReplaceArrangement(event.userData.toReplace, event.userData.replaceBy);
            this.scene.render();
        });

        eventsManager.instance.addListener(Events.SELECTED_COATING_REPLACED, (event: any) => {
            this.ReplaceCoating(event.userData.entity, event.userData.toReplace, event.userData.replaceBy, event.userData.isCredence);
            this.scene.render();
        });

        eventsManager.instance.addListener(Events.SELECTED_KITCHEN_COATING_REPLACED, (event: any) => {
            this.ReplaceKitchenCoating(event.userData.entity, event.userData.replaceBy, event.userData.topBottoms, event.userData.topBottomsConstrained);
            this.scene.render();
        });

        eventsManager.instance.addListener(Events.SELECTED_KITCHEN_HANDLE_REPLACED, (event: any) => {
            this.ReplaceKitchenHandle(event.userData.entities, event.userData.handle);
            this.scene.render();
        });

        eventsManager.instance.addListener(Events.SELECTED_PLINTH_COATING_REPLACED, (event: any) => {
            let entities = event.userData.entities;
            let coating = event.userData.coating;
            for (let i = 0; i < entities.length; ++i) {
                let entity = entities[i];
                let coatings = entity.getComponents(ComponentConstants.ComponentType.Coating);
                for (let j = 0; j < coatings.length; j++) {
                    if (coatings[j].hangType === Coating.HangType.kitchenplinth) {
                        // Remove previous coating
                        entity.removeComponent(coatings[j]);
                        break;
                    }
                }
                entity.addComponent(coating);
            }
            eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: entities[0].scene });
            eventsManager.instance.dispatch(Events.PROJECT_REFRESH_HULL);
        });

        eventsManager.instance.addListener(Events.CHANGE_KITCHEN_STYLE, (event: any) => {
            let command = new ChangeKitchenStyleCommand(this, event.userData.entities, event.userData.style, event.userData.coatingId);
            command.execute();
            this.scene.render();
        });

        eventsManager.instance.addListener(Events.TRANSLATION_MODE, () => {
            this.scene.gizmo.mode = 'translate';
            this.scene.gizmo.showX = true;
            this.scene.gizmo.showY = true;
            this.scene.gizmo.showZ = true;
            this.scene.gizmo.space = 'local';
        });

        eventsManager.instance.addListener(Events.ROTATION_MODE, () => {
            this.scene.gizmo.mode = 'rotate';
            this.scene.gizmo.showX = false;
            this.scene.gizmo.showY = false;
            this.scene.gizmo.showZ = true;
            this.scene.gizmo.space = 'world';
        });

        eventsManager.instance.addListener(Events.HIDE_ARRANGEMENT, () => {
            this._hideSelectedObject();
        });

        eventsManager.instance.addListener(Events.RHINOV_FORMAT_RESETED, () => {
            eventsManager.instance.dispatch(Events.EXIT_EDIT_MODE);
            this.scene.detachSelection();
            this.scene.toggleSelection(this.scene.selection(), false);
            this.scene.selection().splice(0, this.scene.selection().length);
            this.scene.gizmo.visible = false;
        });

        eventsManager.instance.addListener(Events.START_EDIT_MODE, () => {
        });

        eventsManager.instance.addListener(Events.EXIT_EDIT_MODE, () => {
            if (this.scene.outlinePass) {
                let previousSelectedObjects = this.scene.outlinePass.selectedObjects.slice();
                this.scene.outlinePass.selectedObjects = [];
                previousSelectedObjects.forEach((object: any) => {
                    object.traverse((item: any) => {
                        this.scene.setLayer(item, 0);
                    });
                });
            }
            this.selectedNode = [];
        });

        eventsManager.instance.addListener(Events.COMMAND_EXECUTED, (event: any) => {
            switch (event.userData.name) {
                case Commands.CommandEnum.EditEntityCommand:
                    eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: this.scene.savaneScene });
                    break;
            }
        });
    }

    HandleKeyboard = (event: KeyboardEvent) : void => {
        let camera = this.scene.camera;
        if (!camera) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        this.scene.render();
    }

    removeGizmoFromIntersectionResults = (intersects: Array<any>) : Array<any> => {
        return this.scene.removeGizmoFromIntersectionResults(intersects, true);
    }

    _hideObject = (node: WebglFurniture) : void => {
        if (node.entity.hidden) {
            node.object.traverse((child: any) => {
                if (child.isMesh) {
                    child.onBeforeRender = function(renderer: any, scene: any, camera: any, geometry: any, material: any, group: any) {
                        if ((material as any).wasTransparent === undefined && material.transparent) {
                            (material as any).wasTransparent = material.transparent;
                            (material as any).initialOpacity = material.opacity;
                        } else {
                            (material as any).wasTransparent = false;
                        }

                        material.transparent = true;
                        material.opacity = 0.3;
                    }

                    if (Array.isArray(child.material)) {
                        for (let i = 0; i < child.material.length; ++i) {
                            child.material[i].needsUpdate = true;
                        }
                    } else {
                        child.material.needsUpdate = true;
                    }
                }
            });

        } else {
            node.object.traverse((child: any) => {
                if (child.isMesh) {
                    child.onBeforeRender = function(renderer: any, scene: any, camera: any, geometry: any, material: any, group: any) {
                        if ((material as any).wasTransparent) {
                            material.opacity = (material as any).initialOpacity;
                        } else {
                            material.transparent = false;
                            material.opacity = 1;
                        }
                    }
                }
            });
        }
    }

    _hideSelectedObject = () : void => {
        for (let i = 0; i < this.selectedNode.length; ++i) {
            let node = this.selectedNode[i];
            node.entity.hidden = !node.entity.hidden;
            this._hideObject(node);
        }
        eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: this.scene.savaneScene });
        this.scene.render();
    }

    SelectArrangement = (point: {x: number, y: number}) : void => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.removeGizmoFromIntersectionResults(intersects);

        if (intersects.length === 0) {
            this.scene.render();
            return;
        }

        // Try to get the savane entity id stored into the userdata of the object
        let id = intersects[0].object.parent!.userData.id;

        // This is for objects that do not have VLP they'll only have the default mesh instead of the vlp itself, do not search for parent then
        if (id === undefined) {
            id = intersects[0].object.userData.id;
        }

        this.scene.detachSelection();
        this.scene.toggleSelection(this.scene.selection(), false);
        this.scene.selection().splice(0, this.scene.selection().length);
        this.scene.gizmo.visible = false;
        if (id !== undefined) {
            let node = this.scene.getPlanEntity(id) as WebglFurniture;

            let isKitchenEntity = this._isKitchenArrangement(node.entity);
            if (!node.entity.customization || isKitchenEntity) {this.selectedNode = [node];
                eventsManager.instance.dispatch(Events.SELECTED_ARRANGEMENT, { entity: node.entity, point: point, isKitchenEntity: isKitchenEntity });

                this.scene.gizmo.visible = true;
                if (node.entity.parent instanceof ArrangementGroup && node.entity.parent.masterObjectId === node.entity.objectId) {
                    this.scene.selection().push(node.entity.parent);
                } else {
                    this.scene.selection().push(node.entity);
                }
                this.scene.toggleSelection(this.scene.selection(), true);
                eventsManager.instance.dispatch(Events.START_EDIT_MODE);
            }
        } else {
            eventsManager.instance.dispatch(Events.EXIT_EDIT_MODE);
        }

        this.scene.render();
    }

    ReplaceArrangement = (toReplace: ArrangementObject, replaceBy: ArrangementObject) : void => {
        let command = new ReplaceArrangementCommand(this, toReplace, replaceBy, false);
        command.execute();
    }

    SelectCoating = (point: {x: number, y: number}) : boolean => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.removeGizmoFromIntersectionResults(intersects);

        let previousSelectedObjects;
        if (this.scene.outlinePass) {
            previousSelectedObjects = this.scene.outlinePass.selectedObjects.slice();
            this.scene.outlinePass.selectedObjects = [];
            previousSelectedObjects.forEach((object: any) => {
                object.traverse((item: any) => {
                    this.scene.setLayer(item, 0);
                });
            });
        }

        if (intersects.length === 0) {
            this.scene.render();
            return false;
        }

        let intersect = intersects[0];
        // hull entity
        let entity = null;
        let childs: any[] = [];
        if (!entity && this.scene.floorGeneratorHull) {
            entity = this.scene.floorGeneratorHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.floorGeneratorHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.dynamicHull) {
            entity = this.scene.dynamicHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.dynamicHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.staticHull) {
            entity = this.scene.staticHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.staticHull.getChildsFromId(entity.id);
            }
        }

        let result = false;
        if (entity && (entity.isWallEntity() || entity.isRoomEntity() || entity.isJoineryEntity() ||
                entity.isGeometryPrimitiveEntity() || entity.isFloorEntity() || this._isTechnicalElement(entity as any) || this._isWorktopEntity(entity))) {
            let node = this.scene.getPlanEntity(entity.id);

            let coatingArea = intersect.object.name.startsWith("CoatingArea");
            let credence = intersect.object.name.startsWith("Credence");

            if (this._isWorktopEntity(entity)) {
                childs = childs.filter(function(item) {
                    if (credence) {
                        return item.name.startsWith("Credence");
                    } else {
                        return item.name.startsWith("Worktop");
                    }
                });
            }

            if (this.scene.outlinePass) {
                if (previousSelectedObjects!.indexOf(intersect.object) !== -1) {
                    return false;
                }
            } else if (this.selectedNode.indexOf(node) !== -1) {
                return false;
            }
            this.selectedNode = [node];

            if (this.scene.outlinePass) {
                this.scene.outlinePass.selectedObjects = childs;
            }
            // coating
            let hangType = intersect.object.name.indexOf("Direct") !== -1 ? Coating.HangType.wallDirect : Coating.HangType.wallUndirect;
            if (entity.isWallEntity()) {
                if (intersect.object.name.indexOf("Slope") !== -1) {
                    if (intersect.object.name.indexOf("Direct") === -1) {
                        hangType = Coating.HangType.slopeUndirect;
                    }
                    else {
                        hangType = Coating.HangType.slopeDirect;
                    }
                }
            } else if (entity.isRoomEntity() || entity.isFloorEntity()) {
                hangType = intersect.object.name.indexOf("axo") !== -1 ? Coating.HangType.ceiling : Coating.HangType.floor;
            } else if (entity.isJoineryEntity()) {
                hangType = Coating.HangType.joinery;
            } else if (entity.isTechnicalElementEntity() || entity.isGeometryPrimitiveEntity()) {
                hangType = Coating.HangType.technicalElement;
            } else if (entity.isWorktopEntity()) {
                hangType = Coating.HangType.floor;
            }
            if (coatingArea) {
                let coatingAreaParse = intersect.object.name.split('_');
                let coatingAreaNb = Number(coatingAreaParse[2]) - 1;
                let coatings = entity.getComponents(ComponentConstants.ComponentType.CoatingArea);
                eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: coatings[coatingAreaNb], point: point });
                result = true;
            }
            else if ((intersect.object as any).coatingId) {
                //Check coating
                let coatings: any[] = [];
                if (coatingArea == false && credence == false) {
                    coatings = coatings.concat(entity.getComponents(ComponentConstants.ComponentType.Coating));
                } else if (credence) {
                    coatings = coatings.concat(entity.getComponents(ComponentConstants.ComponentType.Credence));
                }
                if (coatings.length == 0) {
                    coatings = entity.getComponents(ComponentConstants.ComponentType.FloorCoatingArea);
                }
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if ((intersect.object as any).coatingId !== coatings[i].coatingId) {
                            continue;
                        }
                        if (coatings[i].hangType !== hangType) {
                            continue;
                        }
                        eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: coatings[i], isCredence: credence, point: point });
                        result = true;
                        break;
                    }
                }
            } else if (entity.isJoineryEntity() || entity.isTechnicalElementEntity() || entity.isGeometryPrimitiveEntity()) {
                let coatings = entity.getComponents(ComponentConstants.ComponentType.Coating);
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if ((coatings[i] as any).hangType !== hangType && (coatings[i] as any).hangType !== Coating.HangType.usemtl) {
                            continue;
                        }
                        eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: coatings[i], point: point });
                        result = true;
                        break;
                    }
                } else {
                    eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: { hangType: hangType, entity: entity }, point: point });
                    result = true;
                }
            } else {
                eventsManager.instance.dispatch(Events.SELECTED_COATING, { entity: entity, coating: { hangType: hangType, entity: entity }, point: point });
                result = true;
            }
        }
        this.scene.render();
        return result;
    }

    ReplaceCoating = (entity: Entity, toReplace: Coating, replaceBy: Coating, isCredence: boolean) : void => {
        let command = new ReplaceCoatingCommand(this, entity, toReplace, replaceBy, isCredence);
        command.execute();
    }

    ReplaceKitchenCoating = (entity: ArrangementObject, replaceBy: Coating, topBottoms: any, topBottomsConstrained: any) : void => {
        let command = new ReplaceKitchenCoatingCommand(this, entity, replaceBy, topBottoms, topBottomsConstrained)
        command.execute();
    }

    ReplaceKitchenHandle = (entities: Array<ArrangementObject>, handle: any) : void => {
        async.eachSeries(entities, (entity: any, callback: any) => {
            if (handle) {
                createAssetEntity(_ASSET_TYPE.ARRANGEMENTS, handle, true, (handleEntity: any) => {
                    this.scene.removeEntity(entity);
                    entity.populateWithHandles(handleEntity, entity.handleSide);
                    this.scene.addEntityTree(entity, () => {
                        callback();
                    });
                });
            } else {
                this.scene.removeEntity(entity);
                entity.handleAsset = '';
                entity.clearAllHandles(entity.handleSide);
                this.scene.addEntityTree(entity, () => {
                    callback();
                });
            }
        }, () => {
            this.scene.render();
            eventsManager.instance.dispatch(Events.RHINOV_FORMAT_UPDATED, { scene: entities[0].scene, command: null });
        });
    }

    HandleMouseDown = (event: MouseEvent) : void => {
        if (this.scene.gizmo.dragging) {
            return;
        }

        if (event.button === 0) {
            let coatingSelected = this.SelectCoating({
                x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
            });
            if (!coatingSelected) {
                this.SelectArrangement({
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                });
            } else {
                this.scene.detachSelection();
                this.scene.toggleSelection(this.scene.selection(), false);
                this.scene.selection().splice(0, this.scene.selection().length);
                this.scene.gizmo.visible = false;
            }
        }
    }

    Hovering = (event: MouseEvent) => {
        if (this.scene.gizmo.dragging) {
            return;
        }

        this._hovering({
            x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
            y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
        });
    }

    Resize = () : void => {
        if (this.hoverOutlinePass) {
            this.hoverOutlinePass.setSize(this.divWebgl.clientWidth, this.divWebgl.clientHeight);
        }
    }

    UpdateCamera = () : void => {
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        if (this.hoverOutlinePass) {
            this.hoverOutlinePass.renderCamera = camera;
        }
        this.selectedNode = [];
        if (this.scene.outlinePass) {
            this.scene.outlinePass.selectedObjects = [];
        }
        this.scene.hideExcludedObject(this.scene.camera);
    }

    _isKitchenArrangement = (entity: ArrangementObject) : boolean => {
        if (entity.objectType === SceneConstants.ArrangementType.kitchenFurnitureBottom ||
            entity.objectType === SceneConstants.ArrangementType.kitchenFurnitureTop ||
            entity.objectType === SceneConstants.ArrangementType.kitchenFurnitureColumn) {
            return true;
        }
        else {
            return false;
        }
    };

    _isWorktopEntity = (entity: Entity) : boolean => {
        if (entity.entityType === SceneConstants.EntityType.WorkTop) {
            return true;
        }

        return false;
    }

    _isTechnicalElement = (entity: TechnicalElement) : boolean => {
        if (entity.isTechnicalElementEntity() == false) return false;
        if (entity.objectId === SceneConstants.TechnicalElementType.pole ||
            entity.objectId === SceneConstants.TechnicalElementType.radiator ||
            entity.objectId === SceneConstants.TechnicalElementType.rosette ||
            entity.objectId === SceneConstants.TechnicalElementType.beam ||
            entity.objectId === SceneConstants.TechnicalElementType.frame ||
            entity.objectId === SceneConstants.TechnicalElementType.wallDecoration) {
            return true;
        }

        return false;
    }

    _hovering = (point: {x: number, y: number}) => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);

        if (this.hoverOutlinePass) {
            this.hoverOutlinePass.hiddenEdgeColor = new THREE.Color("#000000").convertSRGBToLinear();
        }

        this._clearHovering();

        if (intersects.length === 0) {
            this.scene.render();
            return;
        }

        let intersect = intersects[0];

        let id = intersect.object.parent!.userData.id;

        // This is for objects that do not have VLP they'll only have the default mesh instead of the vlp itself, do not search for parent then
        if (id === undefined) {
            id = intersect.object.userData.id;
        }

        if (id !== undefined) {
            // Try to get the 3D node from the 3D entity list
            let node = this.scene.getPlanEntity(id) as WebglFurniture;

            if (!node.entity.customization || this._isKitchenArrangement(node.entity)) {
                if (this.hoverOutlinePass) {
                    this.hoverOutlinePass.selectedObjects.push(node.object);
                }
            }
        }

        if (id === undefined) {
            // hull entity
            let childs: any[] = [];
            let entity = null;
            if (!entity && this.scene.floorGeneratorHull) {
                entity = this.scene.floorGeneratorHull.getEntityFromChild(intersect.object);
                if (entity) {
                    childs = this.scene.floorGeneratorHull.getChildsFromId(entity.id);
                    if (this.hoverOutlinePass) {
                        this.hoverOutlinePass.hiddenEdgeColor = new THREE.Color("#8EC5B7").convertSRGBToLinear();
                    }
                }
            }
            if (!entity && this.scene.dynamicHull) {
                entity = this.scene.dynamicHull.getEntityFromChild(intersect.object);
                if (entity) {
                    childs = this.scene.dynamicHull.getChildsFromId(entity.id);
                    if (this.hoverOutlinePass) {
                        this.hoverOutlinePass.hiddenEdgeColor = new THREE.Color("#8EC5B7").convertSRGBToLinear();
                    }
                }
            }
            if (!entity && this.scene.staticHull) {
                entity = this.scene.staticHull.getEntityFromChild(intersect.object);
                if (entity) {
                    childs = this.scene.staticHull.getChildsFromId(entity.id);
                    if (this.hoverOutlinePass) {
                        this.hoverOutlinePass.hiddenEdgeColor = new THREE.Color("#8EC5B7").convertSRGBToLinear();
                    }
                }
            }

            if (entity && (entity.isWallEntity() || entity.isRoomEntity() || entity.isJoineryEntity() ||
                    entity.isFloorEntity() || this._isTechnicalElement(entity as any) || this._isWorktopEntity(entity))) {
                if (this.hoverOutlinePass) {
                    this.hoverOutlinePass.selectedObjects = childs;
                }
            }
        }

        this.scene.render();
    }

    _clearHovering = () : void => {
        if (this.hoverOutlinePass) {
            this.hoverOutlinePass.selectedObjects = [];
        }
    }

}
